import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import qapi from 'views_shared/app/javascript/api';

import TranscriptPanel from './TranscriptPanel';

// Cap sent-image previews so they don't dominate the thread; clicking opens the full image.
const IMAGE_PREVIEW_MAX_PX = 150;

// Cap the non-image file card so a long filename doesn't stretch it across the thread.
const FILE_CARD_MAX_WIDTH_PX = 200;

// Cap the inline player so a video doesn't dominate the thread.
const VIDEO_PREVIEW_MAX_PX = 280;

// iOS WebKit wedges React-reconciled thread players (loader stalls at t=0 with a full buffer, no
// error), while an identical imperatively-created element plays — verified per-variant on device.
// On iOS the activated player is therefore built outside React's reconciler.
const IS_IOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Cloudinary builds the transformed MP4 derivative lazily on first request, so a just-sent video
// can 404 for a second or two until it's ready. Retry the player a few times before giving up.
const MAX_VIDEO_RETRIES = 5;
const VIDEO_RETRY_MS = 2000;

// Transcription runs in the background after send and can take minutes (Sidekiq queue + Cloudinary
// building the mp3 derivative + Whisper + retries). Poll with a backoff from 3s toward 30s so the
// toggle still appears for slow runs (~5 min budget) without a burst of early requests.
const MAX_TRANSCRIPT_POLLS = 15;
const TRANSCRIPT_POLL_BASE_MS = 3000;
const TRANSCRIPT_POLL_MAX_MS = 30000;
const transcriptPollDelay = (attempt) => Math.min(TRANSCRIPT_POLL_BASE_MS * (attempt + 1), TRANSCRIPT_POLL_MAX_MS);

// Function to determine the FontAwesome class based on file extension
const getFileIconClass = (extension) => {
  switch (extension) {
    case 'pdf':
      return 'fa-file-pdf';
    case 'doc':
    case 'docx':
      return 'fa-file-word';
    case 'pptx':
      return 'fa-file-powerpoint';
    case 'xlsx':
      return 'fa-file-excel';
    case 'mp4':
    case 'mov':
      return 'fa-file-video';
    case 'm4a':
    case 'mp3':
      return 'fa-file-audio';
    case 'html':
      return 'fa-file-code';
    case 'zip':
      return 'fa-file-archive';
    case 'ai':
      return 'fa-file-image';
    default:
      return 'fa-file';
  }
};

function Attachment({ attachment }) {
  const [serverAttachment, setServerAttachment] = useState(null);
  const [videoRetry, setVideoRetry] = useState(0);
  const [transcriptPoll, setTranscriptPoll] = useState(0);
  // Thread videos render as posters until tapped: N mounted preload="metadata" players wedge on
  // iOS (loader suspends after metadata and never wakes: play -> waiting@0 forever) and pile up
  // against WebKit's decoder limits. Mount-on-tap with preload="auto" sidesteps both.
  const [videoActivated, setVideoActivated] = useState(false);
  const pendingSeekRef = useRef(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const videoRetryTimer = useRef(null);
  const transcriptPollTimer = useRef(null);
  const videoRef = useRef(null);

  // Optimistic attachments don't have a server row yet — skip the fetch entirely; the next sync
  // will replace this with a real row (where `pending` may still be set for the brief moment the
  // signed-URL fetch is in flight, so the spinner text reads "Uploading…" instead of "Loading…").
  useEffect(() => {
    if (String(attachment.id).startsWith('optimistic-')) {
      return undefined;
    }

    qapi.attachments.find(attachment.id).then(({ data }) => {
      if (data) {
        setServerAttachment(data);
      }
    });
    return undefined;
  }, [attachment]);

  useEffect(() => () => clearTimeout(videoRetryTimer.current), []);

  // Poll while transcription is pending/processing so the toggle appears without a reload.
  const transcriptStatus = serverAttachment?.transcript?.status;
  useEffect(() => {
    const waiting = transcriptStatus === 'pending' || transcriptStatus === 'processing';
    if (!waiting || transcriptPoll >= MAX_TRANSCRIPT_POLLS) {
      return undefined;
    }

    transcriptPollTimer.current = setTimeout(() => {
      setTranscriptPoll((n) => n + 1);
      // Lean status endpoint: no signed-URL regen, no usage tracking. Merge only the transcript so
      // the <video> src stays put (swapping it would reload the player and drop an in-flight seek).
      qapi.attachments.transcriptStatus(attachment.id).then(({ data }) => {
        if (data) {
          setServerAttachment((prev) => (prev ? { ...prev, transcript: data.transcript } : prev));
        }
      });
    }, transcriptPollDelay(transcriptPoll));
    return () => clearTimeout(transcriptPollTimer.current);
  }, [transcriptStatus, transcriptPoll, attachment.id]);

  // Playback can fail for two reasons: the transformed MP4 derivative isn't built yet (404 on a
  // just-sent clip), or the signed URL's short-lived token has expired (thread left open a while).
  // Both recover the same way — re-fetch a fresh signed URL and re-mount the player — so an expired
  // link self-heals invisibly rather than showing a broken video. Bounded so a genuinely dead asset
  // doesn't loop forever.
  const handleVideoError = () => {
    if (videoRetry >= MAX_VIDEO_RETRIES) {
      return;
    }
    videoRetryTimer.current = setTimeout(() => {
      qapi.attachments.find(attachment.id).then(({ data }) => {
        if (data) {
          setServerAttachment(data);
        }
        setVideoRetry((n) => n + 1);
      });
    }, VIDEO_RETRY_MS);
  };

  // Deref at click time: the <video> remounts on retry (key={videoRetry}) and the ref re-binds.
  const handleSeek = (start) => {
    if (!videoActivated) {
      pendingSeekRef.current = start;
      setVideoActivated(true);
      return;
    }
    const node = videoRef.current;
    if (!node) {
      return;
    }

    const play = () => node.play()?.catch(() => {}); // play() -> promise in browsers, undefined under jsdom
    const canSeekTo = (time) => node.seekable.length > 0 && time <= node.seekable.end(node.seekable.length - 1);

    if (canSeekTo(start)) {
      node.currentTime = start;
      play();
      return;
    }

    // Fresh Cloudinary derivatives are served without range support (seekable=[0,0]), so forward
    // seeks fail; reload for a range-capable response, then seek on canplay (same as a page reload).
    const seekOnceReady = () => {
      node.removeEventListener('canplay', seekOnceReady);
      node.currentTime = start;
      play();
    };
    node.addEventListener('canplay', seekOnceReady);
    node.load();
  };

  // Run a transcript-initiated seek once the tapped player has mounted.
  useEffect(() => {
    if (!videoActivated || pendingSeekRef.current == null) return;
    const start = pendingSeekRef.current;
    pendingSeekRef.current = null;
    const node = videoRef.current;
    if (!node) return;
    const seekOnceReady = () => {
      node.removeEventListener('canplay', seekOnceReady);
      node.currentTime = start;
      node.play()?.catch(() => {});
    };
    node.addEventListener('canplay', seekOnceReady);
  }, [videoActivated]);

  const display = serverAttachment;

  if (!display) {
    return (
      <div
        className="mt-2 card p-2 d-flex flex-row align-items-center gap-2 text-muted"
        style={{ width: 'fit-content' }}
        role="status"
      >
        <div className="spinner-border spinner-border-sm" aria-hidden="true" />
        <small>{attachment.pending ? 'Uploading attachment…' : 'Loading attachment…'}</small>
      </div>
    );
  }

  if (display.video) {
    // Cache-bust per retry so the browser re-requests instead of replaying the cached 404; `key`
    // forces the element to remount and reload the new src.
    const videoSrc = videoRetry === 0
        ? display.private_url
        : `${display.private_url}${display.private_url.includes('?') ? '&' : '?'}_r=${videoRetry}`;

    const transcriptReady = display.transcript?.status === 'ready' && display.transcript.segments?.length > 0;

    return (
      <div className="mt-2" style={{ width: 'fit-content', maxWidth: '100%' }}>
        {/* Panel is a sibling of this wrapper so it can fan out without stretching the meta row. */}
        <div className="transcript-media">
          {videoActivated && IS_IOS ? (
            <div
              // key on videoRetry so a self-heal re-fetch remounts the host and rebuilds the element
              // with the fresh URL — the dataset.mounted guard would otherwise pin the stale src.
              key={videoRetry}
              ref={(host) => {
                if (!host || host.dataset.mounted) return;
                // eslint-disable-next-line no-param-reassign -- marking the host node is the point
                host.dataset.mounted = '1';
                const el = document.createElement('video');
                el.src = videoSrc;
                el.controls = true;
                el.setAttribute('playsinline', '');
                // Same self-heal as the non-iOS player: on a 404 (derivative not ready) or an expired
                // signed URL, re-fetch a fresh URL and remount rather than showing a broken video.
                el.onerror = handleVideoError;
                el.className = 'rounded border d-block';
                el.style.maxHeight = `${VIDEO_PREVIEW_MAX_PX}px`;
                el.style.maxWidth = '100%';
                el.style.background = '#000';
                videoRef.current = el;
                host.appendChild(el);
              }}
            />
          ) : null}
          {videoActivated && !IS_IOS ? (
            <video
              key={videoRetry}
              ref={videoRef}
              src={videoSrc}
              onError={handleVideoError}
              controls
              autoPlay
              playsInline
              preload="auto"
              className="rounded border"
              style={{
 maxHeight: VIDEO_PREVIEW_MAX_PX, maxWidth: '100%', display: 'block', background: '#000',
}}
            >
              <track kind="captions" />
            </video>
          ) : null}
          {!videoActivated ? (
            <button
              type="button"
              className="position-relative border-0 p-0 rounded d-block bg-transparent"
              onClick={() => setVideoActivated(true)}
              aria-label={`Play ${display.filename}`}
            >
              {display.poster_url ? (
                <img
                  src={display.poster_url}
                  alt={display.filename}
                  className="rounded border d-block"
                  style={{ maxHeight: VIDEO_PREVIEW_MAX_PX, maxWidth: '100%' }}
                />
              ) : (
                <span
                  className="rounded border d-block bg-dark"
                  style={{ width: 280, aspectRatio: '16 / 9' }}
                />
              )}
              <span
                className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52, background: 'rgba(255,255,255,.9)' }}
              >
                <i className="fa-solid fa-play text-dark" aria-hidden="true" />
              </span>
            </button>
          ) : null}
          {display.recorded ? (
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-md-between gap-2 gap-md-3 mt-2">
              <div className="d-flex align-items-center gap-1">
                <i
                  className="fa-solid fa-circle-check text-accent-blue-60"
                  aria-hidden="true"
                  style={{ marginTop: '2px' }}
                />
                <span className="text-neutral-100">Recorded live on Qwoted</span>
              </div>
              {transcriptReady && (
                <div className="transcript-toggle form-check form-switch d-flex align-items-center gap-2 m-0 ps-0">
                  <label className="form-check-label text-neutral-100" htmlFor={`transcript-toggle-${attachment.id}`}>
                    View Transcript
                  </label>
                  <input
                    id={`transcript-toggle-${attachment.id}`}
                    className="form-check-input ms-0 mt-0 float-none"
                    type="checkbox"
                    role="switch"
                    checked={showTranscript}
                    onChange={() => setShowTranscript((visible) => !visible)}
                  />
                </div>
              )}
            </div>
          ) : (
            <small className="text-muted text-truncate d-block mt-1">{display.filename}</small>
          )}
        </div>
        {display.recorded && showTranscript && transcriptReady && (
          <TranscriptPanel segments={display.transcript.segments} onSeek={handleSeek} />
        )}
      </div>
    );
  }

  if (display.image) {
    return (
      <div className="mt-2" style={{ width: 'fit-content', maxWidth: '100%' }}>
        <a href={display.private_url} target="_blank" rel="noreferrer" className="d-block">
          <img
            src={display.private_url}
            className="rounded border"
            alt={display.filename}
            style={{
 maxHeight: IMAGE_PREVIEW_MAX_PX, maxWidth: '100%', cursor: 'pointer', display: 'block',
}}
          />
        </a>
        <small className="text-muted text-truncate d-block mt-1">{display.filename}</small>
      </div>
    );
  }

  return (
    <div
      className="mt-2 card d-flex justify-content-center align-items-center"
      style={{ width: 'fit-content', maxWidth: FILE_CARD_MAX_WIDTH_PX }}
    >
      <div className="card-body">
        <a href={display.private_url} target="_blank" rel="noreferrer">
          <i className={`fa ${getFileIconClass(display.extension)} fa-3x`} aria-hidden="true" />
        </a>
      </div>
      <div className="card-footer w-100 text-center">
        <small className="text-muted text-truncate d-block" title={display.filename}>
          {display.filename}
        </small>
      </div>
    </div>
  );
}

Attachment.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pending: PropTypes.bool,
    filename: PropTypes.string,
  }).isRequired,
};

export default Attachment;
